import {
    faArrowTurnDownRight,
    faLocationDot,
    faPlaneDeparture,
} from '@fortawesome/pro-regular-svg-icons';
import Axios, { type CancelTokenSource } from 'axios';
import FlightPriceLoader from './FlightPriceLoader';
import { type CabinClass, cabinClasses } from './types';
import {
    ReserveType,
    type VerifyPathError,
    ViewNotFoundError,
    ViewRedirectError,
} from '@utils/itinerary';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import { type UrlStringParams } from '@search/types';
import {
    type Location,
    type PaxCount,
    PaxType,
    type ReserveInfo,
    SearchMode,
} from '@utils/types';
import { TripType } from '@utils/trip';
import { type FlightPathData, FlightUrlParams } from '@utils/flight';
import {
    AirportsApiApiAirportsGet,
    AirportsApiApiAirportsNearbyGet,
    CabinClassRequest,
} from '@clients/FlightApi';
import type {
    TripShareInfo,
    TripShareLocations,
} from '@itinerary/trip/TripModuleTypes';
import { FlightServiceException } from '@itinerary/trip/flight/FlightModuleTypes';
import { dayConfig } from '#imports';

export class FlightSearchForm extends TripSearchForm {
    flightCabinClass: CabinClass = cabinClasses[0];
    isCapacityTrackCampaign = false;
    airports: Location[] = [];
    cancelFlightSearch: {
        origin: CancelTokenSource | null;
        destination: CancelTokenSource | null;
    } = {
        origin: null,
        destination: null,
    };

    override get domesticDestinations() {
        return this.airports;
    }

    override async loadStaticLocations() {
        if (!this.airports.length) {
            this.isLoadingLocations = true;
            this.airports = await this.loadLocalDomesticAirports();
            this.isLoadingLocations = false;
        }
    }

    get domesticAirportCodesMap() {
        return this.airports.reduce(
            (obj, curr) => {
                obj[curr.code] = true;
                return obj;
            },
            {} as Record<string, boolean>
        );
    }

    override canonicalUrl(path: string) {
        return `https://mrbilit.com${
            this.globalRoutePath ||
            path.replace(this.canonicalRegex, '$1').replace(/\/$/, '')
        }`;
    }

    cabinClassFromCode(code?: string): CabinClassRequest {
        switch (code) {
            case 'Y':
                return CabinClassRequest.Economy;
            case 'C':
                return CabinClassRequest.Business;
            case 'P':
                return CabinClassRequest.EconomyPlus;
            default:
                return CabinClassRequest.All;
        }
    }

    private async validatePathData(
        pathData: FlightPathData
    ): Promise<FlightPathData | null> {
        if (pathData.origin === 'IKA') {
            const domesticAirports = await this.loadLocalDomesticAirports();
            const invalid = domesticAirports.some(
                ({ code }) => code === pathData.destination
            );
            if (invalid) {
                return {
                    ...pathData,
                    origin: 'THR',
                };
            }
        }
        if (pathData.destination === 'IKA') {
            const domesticAirports = await this.loadLocalDomesticAirports();
            const invalid = domesticAirports.some(
                ({ code }) => code === pathData.origin
            );
            if (invalid) {
                return {
                    ...pathData,
                    destination: 'THR',
                };
            }
        }
        if (
            /[A-Z]{3}ALL/.test(pathData.origin) ||
            /[A-Z]{3}ALL/.test(pathData.destination)
        ) {
            const domesticAirports = await this.loadLocalDomesticAirports();
            const invalid =
                domesticAirports.some(
                    ({ code }) => code === pathData.origin.replace('ALL', '')
                ) &&
                domesticAirports.some(
                    ({ code }) =>
                        code === pathData.destination.replace('ALL', '')
                );
            if (invalid) {
                throw new ViewNotFoundError('not found');
            }
        }
        return null;
    }

    override migrateViewPath(pathData: FlightPathData): Promise<void> {
        const {
            origin,
            destination,
            adultCount,
            childCount,
            infantCount,
            departureDate,
            returnDate,
            cabinClass,
        } = pathData;
        const { fullPath, queries } = this.generateViewUrl({
            origin,
            destination,
            departureDate,
            returnDate,
            adultCount,
            childCount,
            infantCount,
            cabinClass,
        });
        return Promise.reject(
            new ViewRedirectError('Path not valid', fullPath, queries)
        );
    }

    override async verifyViewPath(
        path: string,
        pathData: FlightPathData
    ): Promise<void> {
        // one of the origin/destination is IKA and the other is domestic
        // OR both origin and destination is domestic and one has ALL
        const newPathData = await this.validatePathData(pathData);
        if (!newPathData) {
            return await super.verifyViewPath(path, pathData);
        }
        let { departureDate, returnDate } = pathData;
        try {
            await super.verifyViewPath(path, pathData);
            // fixing dates and not same origin/destination
        } catch (e) {
            const err = e as VerifyPathError;
            if (!err.needRedirect) {
                throw err;
            }
            ({ departureDate, returnDate } = err.queries);
        }
        const { fullPath, queries } = this.generateViewUrl({
            origin: newPathData.origin,
            destination: newPathData.destination,
            departureDate,
            returnDate,
            adultCount: pathData.adultCount,
            childCount: pathData.childCount,
            infantCount: pathData.infantCount,
            cabinClass: pathData.cabinClass,
        });
        return Promise.reject(
            new ViewRedirectError('Path not valid', fullPath, queries)
        );
    }

    private get globalRoutePath() {
        const { foreignDestination, foreignOrigin, isForeign } = this;
        if (
            isForeign &&
            foreignDestination?.cityIataCode &&
            foreignOrigin?.cityIataCode
        ) {
            const { fullPath } = this.generateViewUrl({
                origin: foreignOrigin?.cityIataCode,
                destination: foreignDestination?.cityIataCode,
            });
            return fullPath;
        }
        return undefined;
    }

    override findLocationInLocations(
        code: string,
        list: Location[]
    ): Location | null {
        let location: Location | null = null;
        list.forEach(loc => {
            if (!location) {
                if (loc.code === code) {
                    location = loc;
                } else if (loc.subLocations?.length) {
                    location =
                        this.findLocationInLocations(code, loc.subLocations) ??
                        null;
                    if (location) {
                        // the airport cityIataCode is the same as the city cityIataCode
                        location.cityIataCode = loc.cityIataCode;
                    }
                }
            }
        });
        return location;
    }

    override async loadViewUrlParams(params: FlightUrlParams) {
        await super.loadViewUrlParams(params);
        if (this.isForeign) {
            if (
                !this.foreignOrigins.length ||
                !this.foreignOrigins.some(
                    l => l.code === params.origin.substring(0, 3)
                )
            ) {
                await this.loadForeignAirports({
                    target: 'origin',
                    token: params.origin.substring(0, 3),
                });
                const origin = this.findLocationInLocations(
                    params.origin,
                    this.origins
                );
                if (!origin) {
                    throw new ViewNotFoundError('origin not found');
                }
                this.setOrigin(origin);
            }
            if (
                !this.foreignDestinations.length ||
                !this.foreignDestinations.some(
                    l => l.code === params.destination.substring(0, 3)
                )
            ) {
                await this.loadForeignAirports({
                    target: 'destination',
                    token: params.destination.substring(0, 3),
                });
                const destination = this.findLocationInLocations(
                    params.destination,
                    this.destinations
                );
                if (!destination) {
                    throw new ViewNotFoundError('destination not found');
                }
                this.setDestination(destination);
            }

            const { foreignDestination, foreignOrigin } = this;
            if (
                this.globalRoutePath &&
                params.isStatic &&
                (params?.routes?.[0]?.destination !==
                    foreignDestination?.cityIataCode ||
                    params?.routes?.[0]?.origin !== foreignOrigin?.cityIataCode)
            ) {
                return Promise.reject(
                    new ViewRedirectError(
                        'Path not valid',
                        this.globalRoutePath
                    )
                );
            }
        }
    }

    override async parsePath(
        pathData: FlightPathData
    ): Promise<FlightUrlParams> {
        await this.searchLocations(pathData.origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        await this.searchLocations(pathData.destination, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        return new FlightUrlParams(
            [
                {
                    origin: pathData.origin.toUpperCase(),
                    destination: pathData.destination.toUpperCase(),
                    departureDate: pathData.departureDate
                        ? parseDate(pathData.departureDate)!
                        : this.defaultDepartureDate,
                    returnDate: pathData.returnDate
                        ? parseDate(pathData.returnDate)!
                        : undefined,
                },
            ],
            Number(pathData.adultCount) || 1,
            Number(pathData.childCount) || 0,
            Number(pathData.infantCount) || 0,
            this.cabinClassFromCode(pathData.cabinClass),
            !pathData.departureDate
        );
    }

    override get domesticOrigins(): Location[] {
        return this.airports;
    }

    override foreignOrigins: Location[] = [
        {
            code: 'IKA',
            countryName: 'ایران',
            title: 'تهران',
            subtitle: 'IKA',
            cityIataCode: 'THRALL',
        },
    ];

    override swap() {
        super.swap();
        if (this.isForeign) {
            const temp = this.foreignOrigins;
            this.foreignOrigins = this.foreignDestinations;
            this.foreignDestinations = temp;
        }
    }

    handleCapacityTrackCampaign(utmCampaign?: string) {
        if (process.client) {
            this.isCapacityTrackCampaign =
                !!sessionStorage.getItem('utm_campaign_capacity_track') ||
                utmCampaign === 'CapacityTrack';
            if (utmCampaign === 'CapacityTrack') {
                sessionStorage.setItem('utm_campaign_capacity_track', 'true');
            }
        } else {
            this.isCapacityTrackCampaign = utmCampaign === 'CapacityTrack';
        }
    }

    override foreignDestinations = this.foreignOrigins;

    override generateViewQueries(params: UrlStringParams<FlightUrlParams>) {
        const defaultQuery = super.generateViewQueries(params);
        const query = new URLSearchParams(defaultQuery);
        if (params.cabinClass) {
            query.append('cabinClass', params.cabinClass);
        }
        return query;
    }

    override generateViewUrl(params: UrlStringParams<FlightUrlParams>) {
        const query = this.generateViewQueries(params);
        const q = query.toString();
        return {
            fullPath: `/flights/${params.origin}-${params.destination}${
                q ? `?${q}` : ''
            }`,
            queries: Object.fromEntries(query.entries()),
        };
    }

    override getPriceLoader(
        origin: Location,
        destination: Location,
        tripType: TripType
    ): FlightPriceLoader {
        return new FlightPriceLoader({
            tripType,
            from: origin.code,
            to: destination.code,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
        });
    }

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'بین ۲ الی ۱۲ سال',
            count: 0,
        },
        {
            code: PaxType.Infant,
            title: 'نوزاد',
            subtitle: 'کوچکتر از ۲ سال',
            count: 0,
        },
    ];

    get maxCapacity() {
        return 9 - this.childCount - this.infantCount;
    }

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        const infantGap = this.adultCount * 3 - this.childCount;

        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max: this.maxCapacity,
            },
            [PaxType.Child]: {
                count: this.childCount,
                min: 0,
                max: this.adultCount * 3 - this.infantCount,
            },
            [PaxType.Infant]: {
                count: this.infantCount,
                min: 0,
                max: Math.min(infantGap, this.adultCount),
            },
        };
    }

    override updatePaxCount(params: { type: PaxType; count: number }) {
        const paxCount = this.capacityInfo[params.type];
        const initPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        if (
            !initPaxCount ||
            !paxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            if (
                params.type === PaxType.Child ||
                params.type === PaxType.Infant
            ) {
                throw new Error(
                    'به ازای هر بزرگسال 3 کودک یا 2 کودک و یک نوزاد مجاز است.'
                );
            } else {
                throw new Error('حداکثر تعداد مسافران 9 نفر است.');
            }
        }

        if (
            params.type === PaxType.Adult &&
            paxCount.count > params.count &&
            (this.childCount + this.infantCount > (this.adultCount - 1) * 3 ||
                this.infantCount === this.adultCount)
        ) {
            throw new Error(
                'در صورت کاهش بزرگسال، شرط سقف تعداد کودک یا نوزاد رعایت نمی‌شود.'
            );
        }

        if (this.totalCount === 9 && params.count > paxCount.count) {
            throw new Error('حداکثر تعداد مسافران 9 نفر است.');
        }
        initPaxCount.count = params.count;
    }

    override popularForeignOrigins: Location[] = [
        {
            code: 'IKA',
            countryName: 'ایران',
            title: 'تهران',
            subtitle: 'IKA',
            cityIataCode: 'THRALL',
        },
        {
            code: 'ISTALL',
            countryName: 'ترکیه',
            title: 'استانبول',
            subtitle: 'همه فرودگاه‌ها',
            cityIataCode: 'ISTALL',
        },
        {
            code: 'NJF',
            countryName: 'عراق',
            title: 'نجف',
            subtitle: 'NJF',
            cityIataCode: 'NJFALL',
        },
        {
            code: 'BGW',
            title: 'بغداد',
            subtitle: 'BGW',
            countryName: 'عراق',
            cityIataCode: 'BGWALL',
        },
        {
            code: 'DXBALL',
            countryName: 'امارات متحده عربی',
            title: 'دبی',
            subtitle: 'همه فرودگاه‌ها',
            cityIataCode: 'DXBALL',
        },
        {
            code: 'MCT',
            countryName: 'عمان',
            title: 'مسقط',
            subtitle: 'MCT',
            cityIataCode: 'MCTALL',
        },
        {
            code: 'EVN',
            countryName: 'ارمنستان',
            title: 'ایروان',
            subtitle: 'EVN',
            cityIataCode: 'EVNALL',
        },
        {
            code: 'TBS',
            countryName: 'گرجستان',
            title: 'تفلیس',
            subtitle: 'TBS',
            cityIataCode: 'TBSALL',
        },
        {
            code: 'ANK',
            countryName: 'ترکیه',
            title: 'آنکارا',
            subtitle: 'ANK',
            cityIataCode: 'ANKALL',
        },
        {
            code: 'ADB',
            countryName: 'ترکیه',
            title: 'ازمیر',
            subtitle: 'ADB',
            cityIataCode: 'ADBALL',
        },
    ];

    override popularForeignDestinations = this.popularForeignOrigins;

    override popularDomesticOrigins = [
        { code: 'THR', title: 'تهران', subtitle: 'فرودگاه مهرآباد' },
        { code: 'MHD', title: 'مشهد', subtitle: 'فرودگاه شهید هاشمی‌نژاد' },
        { code: 'SYZ', title: 'شیراز', subtitle: 'فرودگاه شهید دستغیب' },
        { code: 'KIH', title: 'کیش', subtitle: 'فرودگاه کیش' },
        { code: 'AWZ', title: 'اهواز', subtitle: 'فرودگاه اهواز' },
    ];

    override popularDomesticDestinations = this.popularDomesticOrigins;

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Flight,
            title: 'هواپیما',
            nameE: 'هواپیمایی',
            icon: faPlaneDeparture,
        };
    }

    override get filterSearchLocations() {
        return this.isDomestic;
    }

    override get viewUrl() {
        const cabinClass = !this.isDomestic
            ? `/${this.flightCabinClass?.code}`
            : '';
        return this.generateViewUrl({
            origin: this.origin?.code,
            destination: this.destination?.code,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
            childCount: this.childCount.toString(),
            infantCount: this.infantCount.toString(),
            cabinClass,
        }).fullPath;
    }

    override get serviceIndexUrl() {
        return this.isDomestic ? '/plane-ticket' : '/international-flights';
    }

    private async loadLocalDomesticAirports() {
        return (await import('~/lib/static/domestic_airports.js')).default;
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode }
    ) {
        if (this.isDomestic || options.mode === SearchMode.Domestic) {
            await this.loadStaticLocations();
        } else {
            await this.loadForeignAirports({
                token: searchKeyword,
                target: options.target,
            });
        }
        await super.searchLocations(searchKeyword, options);
    }

    async loadForeignAirports({
        target,
        token,
    }: {
        token: string;
        target: 'origin' | 'destination';
    }) {
        const prevCancel = this.cancelFlightSearch[target];
        if (typeof prevCancel?.cancel === 'function') {
            prevCancel.cancel();
        }
        try {
            this.isLoadingLocations = true;
            if (token.trim()) {
                const newCancel = Axios.CancelToken.source();
                this.cancelFlightSearch = {
                    origin: target === 'origin' ? newCancel : null,
                    destination: target === 'destination' ? newCancel : null,
                };
                const { data } = await AirportsApiApiAirportsGet(
                    {
                        term: token,
                    },
                    {
                        cancelToken: newCancel.token,
                    }
                );
                const options: Location[] = data.map(city => ({
                    code: city.Code || '',
                    cityIataCode: city?.Code || undefined,
                    title: city.PersianTitle || '',
                    subtitle: city.Airports?.length
                        ? 'همه فرودگاه‌ها'
                        : city.Code || '',
                    countryName: city.CountryPersianTitle || '',
                    icon: faLocationDot,
                    subLocations: city.Airports?.map((ap: any) => ({
                        code: ap.Code,
                        title: ap.CityPersianTitle,
                        subtitle: `فرودگاه ${ap.Title}`,
                        countryName: city.CountryPersianTitle || '',
                        icon: faArrowTurnDownRight,
                    })),
                }));
                switch (target) {
                    case 'origin':
                        this.foreignOrigins = options;
                        break;
                    case 'destination':
                        this.foreignDestinations = options;
                        break;
                }
            } else {
                switch (target) {
                    case 'origin':
                        this.foreignOrigins = [];
                        break;
                    case 'destination':
                        this.foreignDestinations = [];
                        break;
                }
            }
        } catch (error) {
            if (Axios.isCancel(error)) {
                return;
            }
            throw error;
        } finally {
            this.isLoadingLocations = false;
        }
    }

    override duplicateViewQueries(
        currentQueries: UrlStringParams<FlightUrlParams>,
        viewQueries: UrlStringParams<FlightUrlParams>
    ) {
        if (!super.duplicateViewQueries(currentQueries, viewQueries)) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (
            (currentQueries.cabinClass || this.flightCabinClass.code) !==
            (viewQueries.cabinClass || this.flightCabinClass.code)
        ) {
            return false;
        }
        return true;
    }

    override async loadLandingSearchParams(destinationCode: string) {
        let origin: string;
        if (this.isDomestic) {
            origin = destinationCode === 'THR' ? 'IFN' : 'THR';
        } else {
            origin = 'IKA';
        }
        await this.searchLocations(origin, {
            target: 'origin',
            mode: this.searchMode,
        });
        this.setOriginBaseOnCode(origin);
        if (this.isForeign) {
            await this.loadForeignAirports({
                target: 'destination',
                token: destinationCode,
            });
        }
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: this.searchMode,
        });
        this.setDestinationBaseOnCode(destinationCode);
    }

    override async loadSuggestions(params: TripShareInfo & TripShareLocations) {
        try {
            const { data } = await AirportsApiApiAirportsNearbyGet(
                {
                    originLatitude: params.origin.latitude,
                    originLongitude: params.origin.longitude,
                    destinationLatitude: params.destination.latitude,
                    destinationLongitude: params.destination.longitude,
                    departureDate: params.departureDate!.toISOString(),
                },
                {
                    ...params,
                    timeout: 10000,
                }
            );
            if (!data?.length) {
                this.suggestionResult = null;
                return;
            }
            const [nearer] = data;
            this.suggestionResult = {
                viewUrl: this.generateViewUrl({
                    origin: nearer.SourceId ?? '',
                    destination: nearer.DestinationId ?? '',
                    adultCount: params.adultCount.toString(),
                    childCount: params.childCount.toString(),
                    infantCount: params.infantCount.toString(),
                    departureDate: dayConfig
                        .jalali(params.departureDate)
                        .format(formatter()),
                    returnDate: params.returnDate
                        ? dayConfig
                              .jalali(params.returnDate)
                              .format(formatter())
                        : undefined,
                }).fullPath,
                originTitle: nearer.SourceTitle ?? '',
                destinationTitle: nearer.DestinationTitle ?? '',
                minPrice: nearer.MinPrice ?? undefined,
            };
        } catch (e) {
            this.suggestionResult = null;
            if (Axios.isAxiosError(e)) {
                throw new FlightServiceException(e.response?.data?.Message);
            }
            throw new FlightServiceException('');
        }
    }
}
